import PropTypes from 'prop-types';
import { Typography, IconButton, makeStyles } from '@material-ui/core';
import { primaryPalette } from 'styles/theme';
import ConditionalTooltip from '../../conditionalTooltip';
import AsyncListTooltip from '../../asyncListTooltip';
import UserAvatar from '../../userAvatar';
import { ReactComponent as TextReplyIcon } from '../../../../assets/icons/text_snippet.svg';

const useStyles = makeStyles(({ palette: { primary } }) => ({
  scoreButton: {
    height: 35,
    width: 35,
  },
  scoreButtonLabel: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  score: {
    color: primary.indigo,
    lineHeight: '18px',
    flexShrink: 0,
    textAlign: 'center',
    width: 30,
  },
  userScoreWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  userAvatar: {
    width: 'calc(100% - 40px)',
  },
  textReplyWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
  },
  noIndividualScoreWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyCintent: 'center',
    width: 30,
  },
  barContainer: {
    width: 24,
    height: 2,
    borderRadius: 30,
    backgroundColor: primary.bluish5,
  },
  bar: {
    height: 2,
    backgroundColor: primary.bluish1,
  },
}));

const AttributeScore = ({
  translations,
  averageScore = null,
  isTextReply,
  shouldDisableIndividualScores = false,
  onGetScores = () => {},
  isScorePercentage = false,
}) => {
  const classes = useStyles();
  const MAX_SCORE = 10;
  const hasAverageScore = averageScore !== null;

  const calculateBarWidth = val => {
    return (val * 100) / MAX_SCORE;
  };

  const renderIndividualScores = user => {
    const userScore = user.score !== null ? user.score : '/';

    return (
      <div className={classes.userScoreWrapper}>
        <UserAvatar
          className={classes.userAvatar}
          variant="subtitle1"
          user={user}
          small
          caption
        />
        <Typography variant="subtitle1" component="p">
          {userScore}
        </Typography>
      </div>
    );
  };

  if (isTextReply) {
    return (
      <ConditionalTooltip
        className={classes.textReplyWrapper}
        message={translations.textReplyNoAverageScore}
        addTooltip
      >
        <TextReplyIcon />
      </ConditionalTooltip>
    );
  }

  if (shouldDisableIndividualScores && hasAverageScore) {
    return (
      <div className={classes.noIndividualScoreWrapper}>
        <Typography
          className={classes.score}
          style={{
            ...(isScorePercentage
              ? {
                  color:
                    averageScore < 0
                      ? primaryPalette.red1
                      : primaryPalette.bluish1,
                }
              : {}),
          }}
          variant="subtitle1"
        >
          {averageScore}
        </Typography>
        <div className={classes.barContainer}>
          <div
            className={classes.bar}
            style={{
              width: isScorePercentage
                ? `${averageScore}%`
                : `${calculateBarWidth(averageScore)}%`,
              ...(isScorePercentage
                ? {
                    backgroundColor:
                      averageScore < 0
                        ? primaryPalette.red1
                        : primaryPalette.green1,
                  }
                : {}),
            }}
          />
        </div>
      </div>
    );
  }

  if (hasAverageScore) {
    return (
      <AsyncListTooltip
        position="left"
        renderLabelItem={renderIndividualScores}
        getLabelData={onGetScores}
        isDisabled
      >
        <ConditionalTooltip
          message={translations.attributeScore.default}
          addTooltip
        >
          <IconButton
            className={classes.scoreButton}
            classes={{ label: classes.scoreButtonLabel }}
          >
            <Typography
              className={classes.score}
              style={{
                ...(isScorePercentage
                  ? {
                      color:
                        averageScore < 0
                          ? primaryPalette.red1
                          : primaryPalette.bluish1,
                    }
                  : {}),
              }}
              variant="subtitle1"
              component="span"
            >
              {averageScore}
            </Typography>
            <div className={classes.barContainer}>
              <div
                className={classes.bar}
                style={{
                  width: isScorePercentage
                    ? `${averageScore}%`
                    : `${calculateBarWidth(averageScore)}%`,
                  ...(isScorePercentage
                    ? {
                        backgroundColor:
                          averageScore < 0
                            ? primaryPalette.red1
                            : primaryPalette.green1,
                      }
                    : {}),
                }}
              />
            </div>
          </IconButton>
        </ConditionalTooltip>
      </AsyncListTooltip>
    );
  }

  return (
    <Typography className={classes.score} variant="subtitle1" component="span">
      /
    </Typography>
  );
};

AttributeScore.propTypes = {
  translations: PropTypes.object.isRequired,
  isTextReply: PropTypes.bool.isRequired,
  averageScore: PropTypes.number,
  shouldDisableIndividualScores: PropTypes.bool,
  onGetScores: PropTypes.func,
  isScorePercentage: PropTypes.bool,
};

export default AttributeScore;
